import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginCredentials } from '../types/auth';
import { AuthService } from '../services/auth.service';
import { AuthResponse } from '../types/auth';

// Get initial state from localStorage
const getInitialState = (): AuthState => {
  const token = localStorage.getItem('token');
  const memberStr = localStorage.getItem('member');
  const member = memberStr ? JSON.parse(memberStr) : null;
  const selectedMemberStr = localStorage.getItem('selectedMember');
  const selectedMember = selectedMemberStr ? JSON.parse(selectedMemberStr) : null;
  return {
    member: member,
    token: token || null,
    error: null,
    isAuthenticated: !!token,
    loading: false,
    selectedMember: selectedMember,
  };
};

const initialState: AuthState = getInitialState();

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials) => {
    try {
      const response = await AuthService.login(credentials);
      const { token, member } = response;
      localStorage.setItem('token', token);
      localStorage.setItem('member', JSON.stringify(member));
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await AuthService.logout();
    } catch (error: any) {
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('member');
      localStorage.removeItem('selectedMember');
      sessionStorage.clear();
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<AuthResponse>) => {
      const { member, token } = action.payload;
      member.role = member.role || "USER";
      state.member = member;
      state.token = token;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('token', token);
      localStorage.setItem('member', JSON.stringify(member));
    },
    clearCredentials: (state) => {
      state.member = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('member');
      localStorage.removeItem('selectedMember');
      localStorage.removeItem('maatra-active-nav-item');
      sessionStorage.clear();
    },
    setSelectedMember: (state, action: PayloadAction<any>) => {
      state.selectedMember = action.payload;
      localStorage.setItem('selectedMember', JSON.stringify(action.payload));
    },
    clearSelectedMember: (state) => {
      state.selectedMember = null;
      localStorage.removeItem('selectedMember');
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.member = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      localStorage.removeItem('member');
      localStorage.removeItem('selectedMember');
      sessionStorage.clear();
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.member = action.payload.member;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.member = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        state.member = null;
        state.token = null;
        state.isAuthenticated = false;
        state.loading = false;
      });
  },
});

export const { 
  setCredentials, 
  clearCredentials, 
  setSelectedMember,
  clearSelectedMember,
  setError, 
  clearError,
} = authSlice.actions;

export default authSlice.reducer;
